/**
 * Theme Loader Implementation
 * Handles loading theme assets from filesystem/API
 */
import { getThemeRegistry } from './registry';
import { PageDefinition, ThemeComponentRegistry, ThemeDesignSystem, ThemeLoader, ThemeManifest, ThemeMode } from './types';

export class FileSystemThemeLoader implements ThemeLoader {
  private basePath: string;

  constructor(basePath: string = '/themes') {
    this.basePath = basePath;
  }

  async loadManifest(themeId: string): Promise<ThemeManifest> {
    try {
      const response = await fetch(`/api/theme/${themeId}/manifest`);
      if (!response.ok) {
        throw new Error(`Failed to load manifest for theme "${themeId}"`);
      }
      return await response.json();
    } catch (error) {
      throw new Error(`Failed to load theme manifest: ${error}`);
    }
  }

  async loadDesign(themeId: string, mode: ThemeMode): Promise<ThemeDesignSystem> {
    // Check cache first
    const registry = getThemeRegistry();
    const cached = registry.getCachedDesign(themeId, mode);
    if (cached) return cached;

    try {
      const response = await fetch(`/api/theme/${themeId}/design?mode=${mode}`);
      if (!response.ok) {
        throw new Error(`Failed to load design for theme "${themeId}" in ${mode} mode`);
      }
      const design = await response.json();

      // Cache the design
      registry.cacheDesign(themeId, mode, design);

      return design;
    } catch (error) {
      throw new Error(`Failed to load theme design: ${error}`);
    }
  }

  async loadComponents(themeId: string): Promise<ThemeComponentRegistry> {
    try {
      const response = await fetch(`/api/theme/${themeId}/components`);
      if (!response.ok) {
        throw new Error(`Failed to load components for theme "${themeId}"`);
      }
      const componentPaths = await response.json();

      // In Next.js, we'll use dynamic imports
      // This returns component metadata, actual loading happens via dynamic imports
      return componentPaths;
    } catch (error) {
      throw new Error(`Failed to load theme components: ${error}`);
    }
  }

  async loadPages(themeId: string): Promise<PageDefinition[]> {
    try {
      const response = await fetch(`/api/theme/${themeId}/pages`);
      if (!response.ok) {
        throw new Error(`Failed to load pages for theme "${themeId}"`);
      }
      return await response.json();
    } catch (error) {
      throw new Error(`Failed to load theme pages: ${error}`);
    }
  }

  async loadTranslations(themeId: string, language: string): Promise<Record<string, string>> {
    try {
      const response = await fetch(`/api/theme/${themeId}/i18n?lang=${language}`);
      if (!response.ok) {
        throw new Error(`Failed to load translations for theme "${themeId}" in "${language}"`);
      }
      return await response.json();
    } catch (error) {
      throw new Error(`Failed to load theme translations: ${error}`);
    }
  }
}

/**
 * Create appropriate loader based on environment
 * Always use FileSystemThemeLoader (API-based) for client-side
 * For server-side file access, import loader.server.ts directly in API routes
 */
export function createThemeLoader(): ThemeLoader {
  return new FileSystemThemeLoader();
}
